import { PaymentMethodResult, Stripe, StripeElements } from "@stripe/stripe-js";
import { GlobalStateController } from "bai-react-global-state";
import { User as FirebaseUser } from "firebase/auth";
import _ from "lodash";
import React, { useEffect } from "react";
import type StripeTypes from "stripe";

import { InternalServerError } from "apis/errors";
import type { GetWorkspaceStripeDataResponse } from "apis/types";
import {
    ActionPermissionsObject,
    IUserStorageModel,
    IWorkspaceMetadata,
    IWorkspaceUserExtended,
    IWorkspaceUserInvite,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceUserRole
} from "common/interfaces";
import stripeProducts from "common/stripeProducts";
import getObjectHash from "common/utils/getObjectHash";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import * as StripeUtils from "common/utils/stripeUtils";
import { MAX_QUANTITIES } from "common/utils/workspaces/plans";
import Analytics from "js/analytics";
import { calculateTaxAmount, ensureCustomerAndPaymentMethod, getPromotionCode, getWorkspaceStripeData, stripeRadarPaymentCheck, upgradeWorkspacePlan } from "js/controllers/BillingController";
import { getActionPermissionsForWorkspace, getInitialWorkspaceId, getWorkspaceUserInvites, getWorkspaceUsers } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { User } from "js/core/models/user";
import { handleStripeErrors } from "js/core/services/stripe";
import { trackActivity } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import Spinner from "js/react/components/Spinner";

//////////////////////////////////////////////////////////////////
//////////////////// IMPORTANT NOTES /////////////////////////////
// Avoid adding any post or pre-checkout logic here             //
// If you need to do something post-checkout, like redirect     //
// to a specific page, or show a follow-up dialog, then do it   //
// on the higher level                                          //
//////////////////////////////////////////////////////////////////
// Checkout only supports upgrading existing workspace, it does //
// not support implicit workspace creation anymore              //
//////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////

const logger = getLogger(LogGroup.CHECKOUT);

const PROMOTION_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;
const MAX_PROMOTION_CODE_LENGTH = 64;

type BillingInterval = "month" | "year";
type BillingIntervalPriceIds = Partial<Record<BillingInterval, string>>;

export function normalizePromoCode(code: string) {
    if (typeof code !== "string") {
        return null;
    }

    const normalizedPromotionCode = code.trim();
    return normalizedPromotionCode === "" ? null : normalizedPromotionCode;
}

export function validatePromoCode(code: string) {
    code = normalizePromoCode(code);

    if (!code) {
        return "Promotion code is required";
    }

    if (code.length > MAX_PROMOTION_CODE_LENGTH) {
        return `Promotion code must be ${MAX_PROMOTION_CODE_LENGTH} characters or less`;
    }

    if (!PROMOTION_CODE_PATTERN.test(code)) {
        return "Promotion code contains invalid characters";
    }

    return null;
}

function getCheckoutPropsFromLocation() {
    const searchParams = new URLSearchParams(window.location.search);

    let promotionCode = normalizePromoCode(searchParams.get("coupon") ?? searchParams.get("promotionCode"));
    if (validatePromoCode(promotionCode)) {
        promotionCode = null;
    }

    const priceIds = searchParams.getAll("price")
        .flatMap(value => value.split(" "))
        .map(id => id?.trim())
        .filter(Boolean);
    const quantity = parseInt(searchParams.get("quantity") ?? "", 10);
    const minQuantity = Number.isFinite(quantity) ? quantity : 1;
    let billingInterval = searchParams.get("billingTerm") as "month" | "year" | null;
    if (billingInterval && !["month", "year"].includes(billingInterval)) {
        billingInterval = null;
    }
    let tier = searchParams.get("plan") as WorkspacePlanTier | null;
    if (tier && !Object.values(WorkspacePlanTier).includes(tier)) {
        tier = null;
    }

    const checkoutProps = {
        ...(tier ? { tier } : {}),
        ...(billingInterval ? { billingInterval } : {}),
        ...(searchParams.has("displayName") ? { displayName: searchParams.get("displayName") } : {}),
        ...(promotionCode ? { promotionCode } : {}),
        ...(searchParams.has("price") ? { priceIds } : {}),
        ...(searchParams.has("quantity") ? { minQuantity } : {}),
        ...(searchParams.has("workspaceId") ? { workspaceId: searchParams.get("workspaceId") } : {})
    };

    if (!checkoutProps.tier && checkoutProps.displayName) {
        const planAssumedFromDisplayName = checkoutProps.displayName.toLowerCase().includes("pro") ? WorkspacePlanTier.PRO : WorkspacePlanTier.TEAM;
        checkoutProps.tier = planAssumedFromDisplayName;
    }

    return checkoutProps;
}

function getInitialProps({ workspaceId, tier, billingInterval }: { workspaceId?: string, tier?: WorkspacePlanTier, billingInterval?: BillingInterval }) {
    const locationProps = getCheckoutPropsFromLocation();

    tier = tier ?? locationProps.tier;
    billingInterval = billingInterval ?? locationProps.billingInterval;

    let priceId: string;

    const requestedPlans = (locationProps.priceIds ?? [])
        .map(id => StripeUtils.Plans.getRecurringPlanData(id))
        .filter(Boolean)
        .map(planData => {
            if (planData.plan.isDeprecated) {
                return StripeUtils.Plans.getRecurringPlanData(planData.plan.migrateTo);
            }
            return planData;
        })
        .filter(Boolean);

    if (requestedPlans.length) {
        let planData = requestedPlans[0];
        if (billingInterval) {
            planData = requestedPlans.find(planData => planData.plan.interval === billingInterval) ?? planData;
        }
        billingInterval = planData.plan.interval;
        tier = planData.tier;
        priceId = planData.plan.planId;
    } else if (tier) {
        if (billingInterval) {
            priceId = StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
        } else {
            billingInterval = "year";
            priceId = StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
        }
    } else {
        tier = WorkspacePlanTier.PRO;
        billingInterval = "year";
        priceId = StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
    }

    const requestedPriceIdsByBillingInterval = requestedPlans.reduce((priceIdsByBillingInterval, planData) => {
        if (planData.tier === tier && !priceIdsByBillingInterval[planData.plan.interval]) {
            priceIdsByBillingInterval[planData.plan.interval] = planData.plan.planId;
        }

        return priceIdsByBillingInterval;
    }, {} as BillingIntervalPriceIds);

    workspaceId = workspaceId ?? locationProps.workspaceId;

    return {
        ..._.omit(locationProps, ["priceIds", "billingInterval"]),
        workspaceId,
        tier,
        priceId,
        requestedPriceIdsByBillingInterval
    };
}

function getUsedSeatCount(users: IWorkspaceUserExtended[], userInvites: IWorkspaceUserInvite[]) {
    return users.filter(user => user.role !== WorkspaceUserRole.GUEST).length +
        userInvites.filter(invite => invite.role !== WorkspaceUserRole.GUEST).length;
}

export type CheckoutType = "reactivate" | "new_with_trial" | "new_no_trial";

export interface CheckoutControllerState {
    initialized: boolean;
    workspace: {
        metadata: IWorkspaceMetadata;
        users: IWorkspaceUserExtended[];
        userInvites: IWorkspaceUserInvite[];
    }
    tier: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM;
    stripeData: GetWorkspaceStripeDataResponse;
    priceId: string;
    productDisplayName: string | null;
    billingAddress: StripeTypes.Address;
    billingAddressValid: boolean;
    taxRate: number;
    minQuantity: number;
    maxQuantity: number;
    quantity: number;
    taxId: Pick<StripeTypes.TaxId, "type" | "value">;
    promo: StripeTypes.PromotionCode;
    fetching: boolean;
    paymentElement: {
        complete: boolean;
    };
    createPaymentMethod: boolean;
    successfulCheckout: boolean;
}

const initialState: CheckoutControllerState = {
    initialized: false,
    workspace: {
        metadata: null,
        users: [],
        userInvites: []
    },
    tier: null,
    stripeData: {
        customer: null,
        subscription: null,
        paymentMethods: {},
        invoices: {},
        upcomingInvoice: null
    },
    priceId: null,
    productDisplayName: null,
    billingAddress: {
        city: "",
        country: "",
        line1: "",
        line2: "",
        postal_code: "",
        state: ""
    },
    paymentElement: {
        complete: false
    },
    billingAddressValid: false,
    taxRate: 0,
    minQuantity: 1,
    maxQuantity: 1,
    quantity: 1,
    taxId: null,
    promo: null,
    createPaymentMethod: false,
    fetching: false,
    successfulCheckout: false
};

export interface OnSuccessParams {
    workspaceId: string;
    tier: WorkspacePlanTier;
    priceId: string;
}

class CheckoutController extends GlobalStateController<CheckoutControllerState> {
    private _firebaseUser: FirebaseUser;
    private _userModel: IUserStorageModel;
    private _runSequentially: SequentialRunner;
    private _onSuccess: (params: OnSuccessParams) => void;
    private _onCancel?: () => void;
    private _requestedPriceIdsByBillingInterval: BillingIntervalPriceIds = {};

    public get initialized() {
        return this._state.initialized;
    }

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    get usedSeatCount() {
        const { users, userInvites } = this._state.workspace;
        return getUsedSeatCount(users, userInvites);
    }

    get customer() {
        const { customer } = this._state.stripeData;
        return customer;
    }

    get state() {
        return this._state;
    }

    protected _getAvailableTrialDays(tier: WorkspacePlanTier) {
        const { workspace: { metadata } } = this._state;

        if (["active", "past_due"].includes(metadata?.subscriptionState?.type)) {
            if (metadata.subscriptionTrialEndsAt && metadata.subscriptionState.subscriptionStatus === "trialing") {
                // Sub is already in trial, return the number of days remaining as it's the sub that will be upgraded
                return Math.ceil((metadata.subscriptionTrialEndsAt - Date.now()) / 1000 / 60 / 60 / 24);
            }

            // Sub is active or past due, but not in trial, no trial will be offered
            return 0;
        }

        if (metadata?.subscriptionState?.type === "terminal") {
            // Has old sub in terminal state, no trial will be offered
            return 0;
        }

        return this._userModel.availableTrialDays?.[tier] ?? 0;
    }

    get availableTrialDays() {
        return this._getAvailableTrialDays(this._state.tier);
    }

    get plansForBillingIntervals() {
        const { tier, priceId } = this._state;

        const { plan } = StripeUtils.Plans.getRecurringPlanData(priceId);
        const getPlan = (billingInterval: BillingInterval) => {
            if (plan.interval === billingInterval) {
                return plan;
            }

            const intervalPriceId = this._requestedPriceIdsByBillingInterval[billingInterval] ??
                StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
            return StripeUtils.Plans.getRecurringPlanData(intervalPriceId).plan;
        };

        return {
            month: getPlan("month"),
            year: getPlan("year")
        };
    }

    get plan() {
        const { priceId } = this._state;
        return StripeUtils.Plans.getRecurringPlanData(priceId).plan;
    }

    get billingInterval() {
        const { priceId } = this._state;
        return StripeUtils.Plans.getRecurringPlanInterval(priceId);
    }

    get productDisplayName() {
        const { priceId, productDisplayName } = this._state;
        return productDisplayName ?? StripeUtils.Plans.getRecurringPlanData(priceId).productName;
    }

    get defaultPaymentMethod() {
        const { paymentMethods } = this._state.stripeData;
        return StripeUtils.PaymentMethod.getDefaultPaymentMethod(this._state.stripeData.subscription, Object.values(paymentMethods));
    }

    get canCheckout() {
        const { billingAddressValid, paymentElement: { complete }, createPaymentMethod, quantity, maxQuantity, minQuantity } = this._state;

        if (!billingAddressValid) {
            return false;
        }

        if (quantity > maxQuantity || quantity < minQuantity) {
            return false;
        }

        if (quantity < this.usedSeatCount) {
            return false;
        }

        if (createPaymentMethod) {
            return complete;
        }

        return complete || !!this.defaultPaymentMethod;
    }

    get canCancel() {
        return !!this._onCancel;
    }

    get workspaceId() {
        const { workspace: { metadata: { id } } } = this._state;
        return id;
    }

    /**
     * Amounts are in USD.
     */
    get amounts() {
        const { taxRate, promo, quantity, stripeData: { subscription } } = this._state;

        let total = (this.plan.amount / 100) * quantity;
        let taxTotal = taxRate * total;
        const promoCoupon = StripeUtils.getCoupon(promo);
        if (promoCoupon) {
            const totalBefore = total;
            if (promoCoupon.amount_off) {
                total = Math.max(total - promoCoupon.amount_off / 100, 0);
            } else {
                total = total - (total / 100) * promoCoupon.percent_off;
            }
            const ratio = totalBefore > 0 ? total / totalBefore : 1;
            taxTotal = taxTotal * ratio;
        }

        const totalWithTax = total + taxTotal;

        const prorationCredit = StripeUtils.Subscription.getProrationCredit(subscription, {
            newPlanAmountDollars: total / quantity,
            newSeats: quantity,
        });

        const totalWithProrationCredit = total - prorationCredit;
        const taxTotalWithProrationCredit = totalWithProrationCredit * taxRate;

        let dueNow: number;
        let dueNext: number;
        let dueNowTax: number;
        let dueNextTax: number;
        if (this.availableTrialDays > 0) {
            dueNow = 0;
            dueNowTax = 0;
            dueNext = totalWithProrationCredit;
            dueNextTax = taxTotalWithProrationCredit;
        } else {
            dueNow = totalWithProrationCredit;
            dueNowTax = taxTotalWithProrationCredit;
            dueNext = total;
            dueNextTax = taxTotal;
        }

        const { month, year } = this.plansForBillingIntervals;

        const annualCostAtMonthlyRate = (month?.amount ?? 0) * 12 / 100;
        const annualCost = (year?.amount ?? 0) / 100;
        const annualSavingsPercentage = (1 - annualCost / annualCostAtMonthlyRate) * 100;
        const monthlyCost = (month?.amount ?? 0) / 100;

        return {
            taxTotal,
            total,
            totalWithTax,
            dueNow,
            dueNowTax,
            dueNext,
            dueNextTax,
            annualSavingsPercentage,
            annualCostAtMonthlyRate,
            annualCost,
            monthlyCost,
            prorationCredit
        };
    }

    get checkoutType(): CheckoutType {
        const { workspace: { metadata } } = this._state;
        if (metadata.subscriptionCanceledAt) {
            return "reactivate";
        } else if (this.availableTrialDays > 0) {
            return "new_with_trial";
        } else {
            return "new_no_trial";
        }
    }

    get labels() {
        const { workspace: { metadata: { name } } } = this._state;

        const checkoutType = this.checkoutType;
        const productDisplayName = this.productDisplayName;

        const disclaimer = t("By providing your card information, you allow Beautiful.ai to charge your card for future payments in accordance with their terms.");
        const cancelLabel = t("Cancel");
        const progressMessage = t("This will only take a moment...");

        switch (checkoutType) {
            case "reactivate": {
                return {
                    title: t("Reactivate \"{{name}}\" {{productDisplayName}} subscription", { name, productDisplayName }),
                    subtitle: null,
                    submitLabel: t("Reactivate"),
                    progressTitle: t("Reactivating subscription"),
                    progressMessage,
                    cancelLabel,
                    disclaimer,
                };
            }
            case "new_no_trial":
                return {
                    title: t("Upgrade to Beautiful.ai {{productDisplayName}}", { productDisplayName }),
                    subtitle: null,
                    submitLabel: t("Upgrade"),
                    progressTitle: t("Upgrading plan"),
                    progressMessage,
                    cancelLabel,
                    disclaimer,
                };
            case "new_with_trial":
                return {
                    title: t("Get started with our free {{productDisplayName}} trial", { productDisplayName }),
                    subtitle: t("Cancel before your trial ends and you won't be charged."),
                    submitLabel: t("Start Free Trial"),
                    progressTitle: t("Starting free trial"),
                    progressMessage,
                    cancelLabel,
                    disclaimer,
                };
        }
    }

    public async initialize({
        workspaceId: requestedWorkspaceId,
        tier: requestedTier,
        createPaymentMethod = false,
        onSuccess,
        onCancel
    }: {
        workspaceId?: string;
        tier?: WorkspacePlanTier;
        createPaymentMethod?: boolean;
        onSuccess: ({ workspaceId, tier }: { workspaceId: string, tier: WorkspacePlanTier }) => void;
        onCancel?: () => void;
    }) {
        await this._runSequentially(async () => {
            logger.info("[CheckoutController] initialize()");

            await this._reset();

            this._firebaseUser = auth().currentUser;

            if (AppController.initialized && AppController.user) {
                this._userModel = AppController.user;
            } else {
                this._userModel = new User({ id: this._firebaseUser.uid }, { autoLoad: false });
                await this._userModel.load();
            }

            let {
                workspaceId,
                tier,
                priceId,
                displayName,
                promotionCode,
                minQuantity,
                requestedPriceIdsByBillingInterval
            } = getInitialProps({ workspaceId: requestedWorkspaceId, tier: requestedTier });

            if (!workspaceId) {
                if (tier === WorkspacePlanTier.PRO) {
                    workspaceId = this._userModel.getDefaultWorkspaceMetadata()?.id;
                    if (workspaceId) {
                        logger.info("[CheckoutController] selected default basic workspace", { userId: this._userModel.id, workspaceId });
                    }
                }

                if (!workspaceId) {
                    // Just gating by owner here instead of actual permissions
                    workspaceId = getInitialWorkspaceId(this._userModel.workspacesMetadata, this._userModel.get("recentWorkspaceId"), [WorkspaceUserRole.OWNER]);
                    if (workspaceId) {
                        logger.info("[CheckoutController] selected initial workspace", { userId: this._userModel.id, workspaceId });
                    } else {
                        throw new Error(t("No workspace found"));
                    }
                }
            } else {
                logger.info("[CheckoutController] selected requested workspace", { userId: this._userModel.id, workspaceId });
            }

            const workspaceMetadata = this._userModel.workspacesMetadata.find(metadata => metadata.id === workspaceId);
            if (!workspaceMetadata) {
                throw new Error("Workspace not found");
            }

            const workspaceActionPermissions = await getActionPermissionsForWorkspace(workspaceId);
            if (!workspaceActionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                throw new Error("User doesn't have permission to manage billing");
            }

            switch (tier) {
                case WorkspacePlanTier.PRO:
                    if (workspaceMetadata.plan.tier !== WorkspacePlanTier.BASIC) {
                        throw new Error(t("You are already paying for a {{planName}} plan.", { planName: workspaceMetadata.plan.name }));
                    }
                    break;
                case WorkspacePlanTier.TEAM:
                    if (![WorkspacePlanTier.BASIC, WorkspacePlanTier.PRO].includes(workspaceMetadata.plan.tier)) {
                        throw new Error(t("You are already paying for a {{planName}} plan.", { planName: workspaceMetadata.plan.name }));
                    }
                    break;
                case WorkspacePlanTier.ENTERPRISE:
                    throw new Error("Enterprise checkout is not supported");
                default:
                    throw new Error("Invalid target tier");
            }

            const [stripeData, workspaceUsers, userInvites] = await Promise.all([
                getWorkspaceStripeData(workspaceId),
                getWorkspaceUsers(workspaceId),
                getWorkspaceUserInvites(workspaceId)
            ]);

            const defaultPaymentMethod = StripeUtils.PaymentMethod.getDefaultPaymentMethod(stripeData.subscription, Object.values(stripeData.paymentMethods));
            const billingAddress = StripeUtils.Customer.getBillingAddress(stripeData.customer, defaultPaymentMethod);

            let taxId: Pick<StripeTypes.TaxId, "type" | "value"> = null;
            const taxIdValue = StripeUtils.Customer.getTaxId(stripeData.customer);
            if (taxIdValue) {
                taxId = {
                    type: StripeUtils.Customer.getTaxIdType(stripeData.customer),
                    value: taxIdValue
                };
            }

            const usedSeatCount = getUsedSeatCount(workspaceUsers, userInvites);
            minQuantity = Math.max(minQuantity ?? 1, usedSeatCount);
            const quantity = minQuantity;

            const maxQuantity = MAX_QUANTITIES[tier];

            this._onSuccess = onSuccess;
            this._onCancel = onCancel;
            this._requestedPriceIdsByBillingInterval = requestedPriceIdsByBillingInterval;

            await this._updateState({
                initialized: true,
                minQuantity,
                quantity,
                maxQuantity,
                workspace: {
                    metadata: workspaceMetadata,
                    users: workspaceUsers,
                    userInvites: userInvites
                },
                tier,
                stripeData,
                priceId,
                billingAddress,
                billingAddressValid: true,
                taxId,
                productDisplayName: displayName,
                createPaymentMethod
            });

            try {
                await Analytics.util.identify(this._firebaseUser);
                const analyticsProps = {
                    current_status: workspaceMetadata.plan.tier,
                    workspace_id: workspaceId,
                    price_id: priceId,
                    min_quantity: quantity,
                    ...(displayName && { plan_display_name: displayName })
                };

                if (tier === WorkspacePlanTier.PRO) {
                    trackActivity("Upgrade", "BillingIntentStart", null, null, analyticsProps, { audit: true });
                }
                if (tier === WorkspacePlanTier.TEAM) {
                    trackActivity("Organization", "BillingIntentStart", null, null, analyticsProps, { audit: false });
                }
            } catch (err) {
                logger.error(err, "[CheckoutController] failed to send BillingIntentStart analytics event");
            }

            if (promotionCode) {
                const { promo } = await this._applyPromoCode(promotionCode, priceId);
                if (promo) {
                    await this._updateState({ promo });
                }
            }
        });
    }

    public async reset() {
        return this._runSequentially(() => this._reset());
    }

    public async applyPromoCode(promoCode: string) {
        return this._runSequentially(async () => {
            const { priceId } = this._state;

            const { promo, error } = await this._applyPromoCode(promoCode, priceId);
            if (error) {
                return error;
            }

            await this._updateState({ promo });
            return null;
        });
    }

    public async setBillingInterval(billingInterval: "month" | "year") {
        return this._runSequentially(async () => {
            const { month, year } = this.plansForBillingIntervals;

            const priceId = billingInterval === "year" ? year.planId : month.planId;

            await this._updateState({ priceId });
        });
    }

    public async setBillingAddress(billingAddress: StripeTypes.Address, billingAddressValid: boolean) {
        return this._runSequentially(async () => {
            await this._updateState({ billingAddress, billingAddressValid });
        });
    }

    public async setPaymentElementState({ complete }: { complete: boolean }) {
        return this._runSequentially(async () => {
            await this._updateState({ paymentElement: { complete } });
        });
    }

    public async setCreatePaymentMethod(createPaymentMethod: boolean) {
        return this._runSequentially(async () => {
            await this._updateState({
                createPaymentMethod,
                paymentElement: { complete: false }
            });
        });
    }

    public async setQuantity(quantity: number) {
        return this._runSequentially(async () => {
            await this._updateState({ quantity });
        });
    }

    public async checkout({ stripe, elements }: { stripe: Stripe, elements: StripeElements }) {
        return this._runSequentially(async () => {
            const {
                workspace,
                workspace: { metadata: { id: workspaceId } },
                quantity,
                tier,
                priceId,
                billingAddress,
                taxId,
                createPaymentMethod,
                productDisplayName,
                promo,
                paymentElement
            } = this._state;

            if (!this.canCheckout) {
                throw new Error("Not ready");
            }

            await this._updateState({ fetching: true });

            try {
                let paymentMethodWasCreated = false;
                let paymentMethod: StripeTypes.PaymentMethod | null = null;
                if (paymentElement.complete || createPaymentMethod) {
                    try {
                        logger.info("[CheckoutController] creating payment method", { workspaceId });
                        paymentMethod = await this._createPaymentMethod({ stripe, elements });
                        paymentMethodWasCreated = true;
                    } catch (err) {
                        logger.error(err, "[CheckoutController] checkout() failed to create payment method");
                        throw err;
                    }
                } else {
                    logger.info("[CheckoutController] using default payment method", { workspaceId, paymentMethodId: this.defaultPaymentMethod?.id });
                    paymentMethod = this.defaultPaymentMethod;
                }

                logger.info("[CheckoutController] ensuring customer and payment method", { workspaceId, paymentMethodId: paymentMethod?.id });
                await ensureCustomerAndPaymentMethod({
                    workspaceId,
                    billingDetails: {
                        address: billingAddress,
                        taxId: taxId ?? undefined
                    },
                    paymentMethodId: paymentMethod.id
                });

                if (this.availableTrialDays > 0 && paymentMethodWasCreated) {
                    logger.info("[CheckoutController] checking payment method with stripe radar", { workspaceId });
                    await stripeRadarPaymentCheck({
                        elements,
                        paymentMethod,
                        workspaceId,
                    });
                }

                logger.info("[CheckoutController] upgrading workspace plan", { workspaceId, quantity, tier, priceId });
                await upgradeWorkspacePlan({
                    workspaceId,
                    quantity,
                    tier,
                    priceId,
                    billingDetails: {
                        address: billingAddress,
                        taxId: taxId ?? undefined
                    },
                    paymentMethodId: paymentMethod.id,
                    promotionCode: promo?.code
                });

                await this._reloadWorkspaceAndStripeData().catch(err => {
                    logger.error(err, "[CheckoutController] checkout() failed to reload workspace and stripe data after successful checkout");
                });

                await this._updateState({ successfulCheckout: true, fetching: false });

                this._onSuccess({
                    workspaceId,
                    tier,
                    priceId
                });
            } catch (err) {
                await this._reloadWorkspaceAndStripeData().catch(err => {
                    logger.error(err, "[CheckoutController] checkout() failed to reload workspace and stripe data after failed checkout");
                });

                await this._updateState({ fetching: false });

                throw err;
            }
        });
    }

    public async cancel() {
        return this._runSequentially(async () => {
            this._onCancel();

            await this._reset();
        });
    }

    protected async _reloadWorkspaceAndStripeData() {
        let metadata: IWorkspaceMetadata;
        let stripeData: GetWorkspaceStripeDataResponse;
        await Promise.all([
            (async () => {
                await this._userModel.loadWorkspacesMetadata([this.workspaceId]);
                metadata = this._userModel.workspacesMetadata.find(metadata => metadata.id === this.workspaceId);
            })(),
            (async () => {
                stripeData = await getWorkspaceStripeData(this.workspaceId);
            })()
        ]);

        await this._updateState(state => ({
            ...state,
            workspace: {
                ...state.workspace,
                metadata
            },
            stripeData
        }));
    }

    protected async _createPaymentMethod({ stripe, elements }: { stripe: Stripe, elements: StripeElements }) {
        const { billingAddress } = this._state;

        const { error } = await elements.submit();
        if (error) {
            throw new Error(error.message);
        }

        const { paymentMethod } = await handleStripeErrors<PaymentMethodResult>(() => stripe.createPaymentMethod({
            elements,
            params: {
                billing_details: {
                    address: billingAddress
                }
            }
        }));

        return paymentMethod as unknown as StripeTypes.PaymentMethod;
    }

    protected async _applyPromoCode(promoCode: string, priceId: string): Promise<{ promo: StripeTypes.PromotionCode, error: string }> {
        try {
            const validationError = validatePromoCode(promoCode);
            if (validationError) {
                return {
                    promo: null,
                    error: validationError
                };
            }

            const promotionCode = await getPromotionCode(promoCode);

            const usageError = this._canUsePromotionCode(promotionCode, priceId);
            if (usageError) {
                return {
                    promo: null,
                    error: usageError
                };
            }

            return {
                promo: promotionCode,
                error: null
            };
        } catch (err) {
            if (err instanceof InternalServerError) {
                logger.error(err, "[CheckoutController] _applyPromoCode() failed to get promotion code");
                throw err;
            }

            return {
                promo: null,
                error: err.message
            };
        }
    }

    protected _canUsePromotionCode(promo: StripeTypes.PromotionCode, priceId: string) {
        const planData = StripeUtils.Plans.getRecurringPlanData(priceId);

        const productPrice = planData.plan.amount;
        const productName = planData.productName;
        const productId = planData.productId;

        const hasTrial = this._getAvailableTrialDays(planData.tier) > 0;

        const coupon = StripeUtils.getCoupon(promo);
        if (coupon?.applies_to?.products && !coupon.applies_to.products.includes(productId)) {
            return t("This coupon can't be applied to a {{productName}} plan", { productName });
        }

        if (promo.restrictions?.minimum_amount && productPrice < promo.restrictions.minimum_amount) {
            return t("This promo can't be applied to bills of less than ${{amount}}", { amount: promo.restrictions.minimum_amount / 100 });
        }

        if (promo.restrictions?.minimum_amount && hasTrial) {
            return t("This promo can't be applied until after your free trial is over");
        }

        if (promo.restrictions?.first_time_transaction && !hasTrial) {
            return t("This promo is only for new users");
        }

        return null;
    }

    protected _stateDidUpdate(prevState: CheckoutControllerState) {
        this._runSequentially(async () => {
            const { billingAddress, taxId, priceId, initialized, promo } = this._state;

            if (!initialized) {
                return;
            }

            if (
                getObjectHash(_.pick(billingAddress, ["country", "postal_code"])) !== getObjectHash(_.pick(prevState.billingAddress, ["country", "postal_code"])) ||
                (taxId?.value ?? "") !== (prevState.taxId?.value ?? "") ||
                priceId !== prevState.priceId
            ) {
                await this._calculateTaxAmountPerSeat();
            }

            if (priceId !== prevState.priceId) {
                if (promo) {
                    const usageError = this._canUsePromotionCode(promo, priceId);
                    if (usageError) {
                        await this._updateState({ promo: null });
                    }
                }
            }
        });
    }

    protected async _reset() {
        logger.info("[CheckoutController] reset()");

        this._firebaseUser = null;
        this._userModel = null;
        this._onSuccess = null;
        this._onCancel = null;
        this._requestedPriceIdsByBillingInterval = {};

        await this._updateState(_.cloneDeep(initialState));
    }

    protected async _calculateTaxAmountPerSeat() {
        const { billingAddress, billingAddressValid, taxId, tier } = this._state;

        if (!billingAddressValid) {
            await this._updateState({ taxRate: 0 });
            return;
        }

        try {
            const { taxAmount } = await calculateTaxAmount({
                amount: this.plan.amount,
                stripeProductId: stripeProducts[tier].productId,
                countryCode: billingAddress.country,
                postalCode: billingAddress.postal_code,
                taxId: taxId?.value
            });

            const taxRate = taxAmount / this.plan.amount;

            await this._updateState({ taxRate });
        } catch (err) {
            logger.error(err, "[CheckoutController] _calculateTaxAmountPerSeat() failed");

            await this._updateState({ taxRate: 0 });
        }
    }
}

const checkoutController = new CheckoutController();

// for lazy import and debug
app.checkoutController = checkoutController;

export default checkoutController;

export const CheckoutControllerInitializer = checkoutController.withState(function CheckoutControllerInitializer({
    children,
    workspaceId,
    tier,
    createPaymentMethod,
    onSuccess,
    onCancel,
    initialized
}: {
    children: React.ReactNode | ((state: CheckoutControllerState) => React.ReactElement),
    workspaceId?: string,
    tier?: WorkspacePlanTier,
    createPaymentMethod?: boolean,
    onSuccess: (params: OnSuccessParams) => void,
    onCancel?: () => void,
    initialized: boolean
}) {
    useEffect(() => {
        checkoutController.initialize({
            workspaceId,
            tier,
            createPaymentMethod,
            onSuccess,
            onCancel
        }).catch(err => {
            logger.error(err, "[CheckoutController] initialize() failed");

            ShowErrorDialog({
                title: t("Oops, something went wrong"),
                message: `${err.message} ${t("Let's take you to your presentation library.")}`,
                onClose: () => window.location.href = "/"
            });
        });

        return () => {
            checkoutController.reset();
        };
    }, []);

    if (initialized) {
        return typeof children === "function" ? children(checkoutController.state) : children;
    }

    return <Spinner />;
});
